import { PageHead } from '../../components/ui/PageHead';
import { PageHero } from '../../components/hero/PageHero';
import { Section } from '../../components/layout/Section';
import { Container } from '../../components/layout/Container';
import { Grid } from '../../components/layout/Grid';
import { Stack } from '../../components/layout/Stack';
import { StatsBar } from '../../components/content/StatsBar';
import { ProcessSteps } from '../../components/content/ProcessSteps';
import { CaseStudyCard } from '../../components/cards/CaseStudyCard';
import { CTABand } from '../../components/cta/CTABand';
import styles from './WhyNAATSPage.module.css';

const differentiators = [
  {
    icon: '🎯',
    title: 'Outcome-First Architecture',
    description:
      'Every architectural decision is made with measurable business outcomes in mind. We define success metrics before we write a single line of infrastructure code.',
  },
  {
    icon: '☁️',
    title: 'True Cloud Agnosticism',
    description:
      'We build on open standards - Delta Lake, Apache Iceberg, Kubernetes - so you are never locked into a single cloud vendor or proprietary platform.',
  },
  {
    icon: '🛡️',
    title: 'Governance by Default',
    description:
      'Data quality, lineage, access controls, and compliance posture are embedded at every layer of the Medallion architecture - not bolted on afterward.',
  },
  {
    icon: '🤝',
    title: 'People Centric',
    description:
      'We put your people first. Our architects embed within your teams to transfer knowledge, build internal capability, and leave your organization stronger - not dependent on us.',
  },
  {
    icon: '⚡',
    title: 'Rapid Time-to-Value',
    description:
      'Production-grade Bronze layer in 30 days. Silver layer governance in 60. Gold-layer AI features in 90. Our phased delivery model is built for speed without sacrifice.',
  },
  {
    icon: '📐',
    title: 'Industry-Specific Patterns',
    description:
      'Pre-built accelerators for HIPAA, SOX, FDA 21 CFR Part 11, and GxP environments - validated compliance patterns that compress months of work into days.',
  },
];

const caseStudies = [
  {
    industry: 'Financial Services',
    title: 'Tier-1 Bank Achieves 90% Reduction in Model Deployment Time',
    summary:
      'We migrated a fragmented on-premises data warehouse to a cloud-native Medallion Lakehouse, enabling the risk team to deploy credit models 90% faster with full SOX-compliant audit trails.',
    result: '90% faster',
    statValue: '90%',
    statLabel: 'Reduction in model deployment cycle time',
  },
  {
    industry: 'Healthcare',
    title: 'Health System Unifies 28 EHR Data Sources in 90 Days',
    summary:
      'A regional health network with 28 disparate EHR systems now operates a single HIPAA-compliant patient data lakehouse - powering population health AI across 1.2M patient records.',
    result: '28 sources unified',
    statValue: '1.2M',
    statLabel: 'Patient records in governed lakehouse',
  },
  {
    industry: 'Pharmaceuticals',
    title: 'Pharma Leader Accelerates FDA Submission Data Package by 60%',
    summary:
      'By implementing GxP-validated Bronze → Silver → Gold pipelines, our client reduced the time to compile regulatory submission data packages by 60% with zero audit findings.',
    result: '60% faster',
    statValue: '0',
    statLabel: 'Audit findings in regulatory submissions',
  },
];

const leadership = [
  {
    name: 'Najeem Adeleke',
    title: 'Founder & Principal Consultant',
    photo: '/img/najm.jpeg',
    bio: 'Ph.D. in Engineering from Penn State. A former engineering leader at Upstart and Vanguard, he specializes in building scalable data platforms, cloud architectures, and enterprise AI systems that accelerate analytics, automation, and business innovation.',
  },
  {
    name: 'Musodiq Bello',
    title: 'Principal Consultant',
    photo: '/img/muso.jpg',
    bio: 'Ph.D. in Computer Science from University of Houston. 20+ years building scalable AI/ML platforms and data pipelines in regulated environments, including 14+ years with GE Healthcare. 10 issued patents and 20+ peer-reviewed publications.',
  },
  {
    name: 'Bashir Agboola',
    title: 'Strategic Advisor',
    photo: '/img/bash.webp',
    bio: 'DBA from Walden University. Former VP, CTO, and Associate CIO at Hospital for Special Surgery. Forbes Technology Council member specializing in enterprise AI governance, healthcare data strategy, and digital transformation across regulated health systems.',
  }
];

export function WhyNAATSPage() {
  return (
    <>
      <PageHead
        title="Why NAATS"
        description="Differentiation, delivery methodology, and outcome-led credibility - why leading enterprises choose NAATS for Medallion Lakehouse and Agentic AI."
      />

      <PageHero
        eyebrow="Why NAATS"
        title="Where Architecture Meets Outcomes"
        description="We combine deep platform engineering with industry-specific compliance expertise to deliver Medallion Lakehouses that actually perform - on time, with measurable ROI."
      />

      <StatsBar />

      {/* Differentiators */}
      <Section bg="page">
        <Container>
          <Stack gap="xl">
            <div className={styles.sectionHeader}>
              <p className={styles.eyebrow}>Our Differentiation</p>
              <h2 className={styles.sectionTitle}>What Makes NAATS Different</h2>
              <p className={styles.sectionDesc}>
                Six principles that define how we architect, deliver, and measure enterprise data
                and AI platforms.
              </p>
            </div>
            <Grid cols={3} gap="md">
              {differentiators.map((d) => (
                <div key={d.title} className={styles.differentiatorCard}>
                  <div className={styles.diffIcon}>{d.icon}</div>
                  <h3 className={styles.diffTitle}>{d.title}</h3>
                  <p className={styles.diffDesc}>{d.description}</p>
                </div>
              ))}
            </Grid>
          </Stack>
        </Container>
      </Section>

      {/* Methodology */}
      <Section bg="subtle">
        <Container>
          <Stack gap="xl">
            <div className={styles.sectionHeader}>
              <p className={styles.eyebrow}>Our Delivery Model</p>
              <h2 className={styles.sectionTitle}>Assess. Architect. Accelerate.</h2>
              <p className={styles.sectionDesc}>
                A repeatable three-phase methodology with defined deliverables and measurable
                outcomes at each milestone.
              </p>
            </div>
            <ProcessSteps />
          </Stack>
        </Container>
      </Section>

      {/* Case Studies */}
      <Section bg="page">
        <Container>
          <Stack gap="xl">
            <div className={styles.sectionHeader}>
              <p className={styles.eyebrow}>Proven Results</p>
              <h2 className={styles.sectionTitle}>Outcomes We've Delivered</h2>
            </div>
            <Grid cols={3} gap="md">
              {caseStudies.map((cs) => (
                <CaseStudyCard key={cs.title} {...cs} />
              ))}
            </Grid>
          </Stack>
        </Container>
      </Section>

      {/* Leadership */}
      <Section bg="subtle">
        <Container>
          <Stack gap="xl">
            <div className={styles.sectionHeader}>
              <p className={styles.eyebrow}>Our Team</p>
              <h2 className={styles.sectionTitle}>The People Behind NAATS</h2>
              <p className={styles.sectionDesc}>
                Seasoned practitioners who have designed and delivered enterprise solutions
                across financial services, healthcare, life sciences, and petroleum industries.
              </p>
            </div>
            <div className={styles.leadershipGrid}>
              {leadership.map((person) => (
                <div key={person.name} className={styles.leaderCard}>
                  <div className={styles.leaderPhotoWrap}>
                    <img src={person.photo} alt={person.name} className={styles.leaderPhoto} />
                  </div>
                  <div className={styles.leaderInfo}>
                    <h3 className={styles.leaderName}>{person.name}</h3>
                    <p className={styles.leaderTitle}>{person.title}</p>
                    <p className={styles.leaderBio}>{person.bio}</p>
                  </div>
                </div>
              ))}
            </div>
          </Stack>
        </Container>
      </Section>

      <CTABand
        heading="Ready to See the NAATS Difference?"
        subheading="Book a session with a NAATS expert to see how we can solve your challenges."
        primaryLabel="Schedule a Review"
        secondaryLabel="See the Platform"
        secondaryTo="/platform"
      />
    </>
  );
}
